import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Play,
  Pause,
  Square,
  FastForward,
  Clock,
  Calendar,
  Loader2
} from 'lucide-react';
import type { BacktestClockTick } from '@/services/backtestService';

export type BacktestState = 'idle' | 'creating' | 'running' | 'paused' | 'stopped';

interface BacktestControlBarProps {
  state: BacktestState;
  clockTick: BacktestClockTick | null;
  speed: number;
  onCreateAndStart: (startDate: string, endDate: string, startTime: string, endTime: string, speed: number) => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onSpeedChange: (speed: number) => void;
}

const SPEED_PRESETS = [1, 10, 50, 100, 500, 1000];

/** Compact inline control bar for backtest mode within the scanner dashboard. */
const BacktestControlBar: React.FC<BacktestControlBarProps> = ({
  state,
  clockTick,
  speed,
  onCreateAndStart,
  onPause,
  onResume,
  onStop,
  onSpeedChange,
}) => {
  // Default to a week ago → yesterday
  const defaultEnd = new Date();
  defaultEnd.setDate(defaultEnd.getDate() - 1);
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() - 8);

  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(fmt(defaultStart));
  const [endDate, setEndDate] = useState(fmt(defaultEnd));
  const [startTime, setStartTime] = useState('04:00');
  const [endTime, setEndTime] = useState('20:00');

  const handleStart = useCallback(() => {
    onCreateAndStart(startDate, endDate, startTime, endTime, speed);
  }, [startDate, endDate, startTime, endTime, speed, onCreateAndStart]);

  const isActive = state === 'running' || state === 'paused';

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 glass rounded-lg border border-border/40">
      {/* Date pickers — only enabled when idle */}
      <div className="flex items-center gap-1.5">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          disabled={isActive || state === 'creating'}
          className="h-7 w-[130px] text-xs px-2 bg-background/60 border border-border/50 rounded-md text-foreground"
        />
        <input
          type="time"
          value={startTime}
          onChange={e => setStartTime(e.target.value)}
          disabled={isActive || state === 'creating'}
          className="h-7 w-[90px] text-xs px-2 bg-background/60 border border-border/50 rounded-md text-foreground"
        />
        <span className="text-xs text-muted-foreground">→</span>
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          disabled={isActive || state === 'creating'}
          className="h-7 w-[130px] text-xs px-2 bg-background/60 border border-border/50 rounded-md text-foreground"
        />
        <input
          type="time"
          value={endTime}
          onChange={e => setEndTime(e.target.value)}
          disabled={isActive || state === 'creating'}
          className="h-7 w-[90px] text-xs px-2 bg-background/60 border border-border/50 rounded-md text-foreground"
        />
      </div>

      {/* Divider */}
      <div className="h-5 w-px bg-border/60" />

      {/* Transport controls */}
      <div className="flex items-center gap-1">
        {state === 'idle' || state === 'stopped' ? (
          <Button
            variant="default"
            size="sm"
            className="h-7 px-3 text-xs gap-1"
            onClick={handleStart}
          >
            <Play className="h-3.5 w-3.5" /> Start
          </Button>
        ) : state === 'creating' ? (
          <Button variant="default" size="sm" className="h-7 px-3 text-xs gap-1" disabled>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Starting…
          </Button>
        ) : state === 'running' ? (
          <Button variant="secondary" size="sm" className="h-7 px-2 text-xs" onClick={onPause}>
            <Pause className="h-3.5 w-3.5" />
          </Button>
        ) : state === 'paused' ? (
          <Button variant="default" size="sm" className="h-7 px-2 text-xs" onClick={onResume}>
            <Play className="h-3.5 w-3.5" />
          </Button>
        ) : null}

        {isActive && (
          <Button variant="destructive" size="sm" className="h-7 px-2 text-xs" onClick={onStop}>
            <Square className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Divider */}
      <div className="h-5 w-px bg-border/60" />

      {/* Speed presets */}
      <div className="flex items-center gap-1">
        <FastForward className="h-3.5 w-3.5 text-muted-foreground" />
        {SPEED_PRESETS.map(s => (
          <Button
            key={s}
            variant={speed === s ? 'default' : 'ghost'}
            size="sm"
            className="h-6 px-1.5 text-[10px] font-mono min-w-[32px]"
            onClick={() => onSpeedChange(s)}
            disabled={!isActive && state !== 'idle' && state !== 'stopped'}
          >
            {s}x
          </Button>
        ))}
      </div>

      {/* Divider */}
      <div className="h-5 w-px bg-border/60" />

      {/* Clock display */}
      <div className="flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        {clockTick ? (
          <>
            <span className="font-mono text-xs tabular-nums text-primary">
              {clockTick.formattedTimeEt}
            </span>
            <Badge variant="outline" className="text-[9px] px-1">
              {clockTick.session}
            </Badge>
            {clockTick.isPaused && (
              <Badge variant="secondary" className="text-[9px] px-1">
                PAUSED
              </Badge>
            )}
          </>
        ) : (
          <span className="text-xs text-muted-foreground font-mono">--/-- --:--:-- --</span>
        )}
      </div>
    </div>
  );
};

export default BacktestControlBar;
